import { Box, Text } from '@chakra-ui/react';
import { useToast } from '@chakra-ui/react';
import React, { useEffect, useRef, useState } from 'react';

import { PopiaNotice } from '@/app/_components/popia-notice/popia-notice';
import {
  ERROR_DESCRIPTION,
  ERROR_TITLE,
} from '@/constants/enquiry-forms/enquiry-forms.constants';
import {
  SPECIAL_FOR_SERVICE,
  SPECIAL_FOR_VEHICLE,
} from '@/constants/specials/specials.constants';
import { useDealersInfo } from '@/hooks/dealers/dealers.hook';
import { useEnquireForm } from '@/hooks/enquire-form/enquire-form.hook';
import { useNewCarSourceFieldConfig } from '@/hooks/enquire-form/use-new-car-source-field-config';
import { useFloatingCTA } from '@/hooks/floating-cta.hook';
import { BrandAndRangesType } from '@/services/filters/new-filters.service';
import { pushFormSubmittedEvent } from '@/services/gtm.service';
import {
  getFloorCodeForSpecials,
  getSpecialSource,
  setSourceCode,
} from '@/services/link.service';
import { createLead } from '@/services/UsedCarsService.service';
import type { PopiaNoticeField } from '@/types/config/config.types';
import {
  NewCar,
  SpecificationsPricesType,
} from '@/types/new-cars/new-cars.types';
import { VehicleType } from '@/types/used-cars/used-cars.types';
import { getUtmData } from '@/utils/getUtmData';

import { EnquireFormNewCars } from './components/enquire-form/enquire-form';
import { ThankYouMessageNewCars } from './components/thank-you-message/thank-you-message';
import Styles from './enquire-now-form-new-cars.module.scss';

enum IsUsedVehicleLead {
  yes = '1',
  no = '0',
}

export const EnquireNowFormNewCars: React.FC<{
  newCars: NewCar[];
  onClose: () => void;
  documentPaiaPopia: PopiaNoticeField;
  brandsAndRanges: BrandAndRangesType;
  dealerName?: string;
}> = ({ newCars, onClose, documentPaiaPopia, dealerName, brandsAndRanges }) => {
  const [isMessageSent, setIsMessageSent] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { leadType, formItem } = useFloatingCTA();

  const toast = useToast();

  const specialVariantMMCode =
    formItem?.type === 'special' ? formItem.special.mmcode : null;
  const specialRangeName =
    formItem?.type === 'special' ? formItem.special.range?.name : null;
  const { getNewCarSourceValue } = useNewCarSourceFieldConfig();
  const specialNewCarId = newCars.find(
    (newCar) =>
      newCar?.specification_prices?.some(
        (spec) => spec.mm_code === specialVariantMMCode,
      ) || newCar.main_title.toLowerCase() === specialRangeName?.toLowerCase(),
  )?.id;

  const [selectedNewCarId, setSelectedNewCarId] = useState<number | null>(
    formItem?.type === 'new-car' && formItem.newCar
      ? formItem.newCar.id
      : specialNewCarId || null,
  );

  const { dealersInfo } = useDealersInfo();
  const carData = newCars.find((car) => car.id === selectedNewCarId);
  const variantMMCode =
    formItem?.type === 'new-car'
      ? formItem.mmCode
      : formItem?.type === 'special'
        ? formItem.special.mmcode || null
        : null;
  const [selectedVariant, setSelectedVariant] =
    useState<SpecificationsPricesType | null>(
      carData
        ? carData.specification_prices.find(
            (variant) => variant.mm_code === variantMMCode,
          ) || null
        : null,
    );
  const { resetEnquireOptions } = useEnquireForm();

  useEffect(() => {
    return () => {
      setIsMessageSent(false);
      resetEnquireOptions();
    };
  }, []);

  const onSubmitForm = async (values: any) => {
    const utm_data = getUtmData();

    setIsLoading(true);
    const dealer = dealersInfo.find(({ id }) => id === values.branch_name);

    let leadData = null;

    if (formItem?.type === 'used-car') {
      const activeEnquireFormVehicle = formItem.usedCar;

      leadData = {
        ...values,
        vehicles: activeEnquireFormVehicle?.id,
        lead_type: leadType,
        dealer_id: activeEnquireFormVehicle?.dealership_id?.id || dealer?.id,
        dealer_name:
          activeEnquireFormVehicle?.dealership_id?.name || dealer?.name,
        dealer_city:
          activeEnquireFormVehicle?.dealership_id?.city?.name ||
          dealer?.city?.name,
        dealer_province:
          activeEnquireFormVehicle?.dealership_id?.province?.name,
        dealer_country:
          activeEnquireFormVehicle?.dealership_id?.city?.country?.name ||
          dealer?.city?.country?.name,
        vehicle_brand: activeEnquireFormVehicle?.brand,
        vehicle_range: activeEnquireFormVehicle?.range_id,
        vehicle_model: activeEnquireFormVehicle?.model,
        vehicle_mmcode: activeEnquireFormVehicle?.mmcode,
        vehicle_kms: activeEnquireFormVehicle?.mileage,
        vehicle_year: activeEnquireFormVehicle?.year,
        vehicle_colour: activeEnquireFormVehicle?.bodyColors,
        vehicle_vin: activeEnquireFormVehicle?.vin,
        vehicle_price:
          +activeEnquireFormVehicle?.nowPrice ||
          +activeEnquireFormVehicle?.price ||
          null,
        vehicle_registration: activeEnquireFormVehicle?.registrationNumber,
        vehicle_drivetrain: activeEnquireFormVehicle?.drivetrain,
        vehicle_transmission: activeEnquireFormVehicle?.transmission,
        vehicle_fuel: activeEnquireFormVehicle?.fuelType,
        vehicle_doors: activeEnquireFormVehicle?.doors,
        vehicle_image: activeEnquireFormVehicle?.mainImage,
        vehicle_images: activeEnquireFormVehicle?.images,
        vehicle_title: activeEnquireFormVehicle?.title,
        vehicle_body_type: activeEnquireFormVehicle?.bodyType,
        vehicle_type: activeEnquireFormVehicle?.type,
        dealership_id:
          activeEnquireFormVehicle?.dealership_id?.id || dealer?.id,
        dealership_name:
          activeEnquireFormVehicle?.dealership_id?.name || dealer?.name,
        contact_firstname: values?.user_name,
        contact_surname: values?.user_name,
        contact_email: values?.email,
        contact_officephone: values?.phone_number,
        contact_cellphone: values?.phone_number,
        comments: values?.message,
        special: '',
        dealer_ref:
          activeEnquireFormVehicle?.dealership_id?.cms_used_dealer_code ||
          dealer?.cms_used_dealer_code,
        dealer_floor:
          activeEnquireFormVehicle?.dealership_id?.cms_used_leads ||
          dealer?.cms_used_leads,
        vehicle_used: IsUsedVehicleLead.yes,
        related_specials: null,
        source: setSourceCode(
          activeEnquireFormVehicle,
          activeEnquireFormVehicle.range_id,
        ),
        utm_source: utm_data?.utm_source || '',
        utm_medium: utm_data?.utm_medium || '',
        utm_campaign: utm_data?.utm_campaign || '',
        utm_content: utm_data?.utm_content || '',
        utm_term: utm_data?.utm_term || '',
        utm_cta: utm_data?.utm_cta || '',
        vehicle_testdrive_location_at_contact:
          values?.location_at_contact || false,
        vehicle_testdrive: leadType === 'test-drive',
        utm_dealership: utm_data?.utm_dealership || '',
        fbclid: utm_data?.fbclid || '',
        gclid: utm_data?.gclid || '',
        dealership_micro_site: dealerName,
      };
    } else if (formItem?.type === 'special') {
      const special = formItem.special;

      const specialDealership = special.dealerships.length
        ? special.dealerships.at(0)?.dealerships_id
        : null;

      leadData = {
        ...values,
        vehicles: null,
        lead_type: formItem.leadType,
        dealer_id: dealer?.id || specialDealership?.id,
        dealer_name: dealer?.name || specialDealership?.name,
        dealer_city: dealer?.city.name || specialDealership?.city?.name,
        dealer_province:
          dealer?.city.name || specialDealership?.city?.province.name,
        dealer_country:
          dealer?.city?.country?.name || specialDealership?.city?.country?.name,
        vehicle_brand: special.brand?.name,
        vehicle_range: special.range?.name,
        vehicle_model: special.model?.name,
        vehicle_mmcode: special?.mmcode || '',
        vehicle_kms: special?.mileage,
        vehicle_year: special?.year || null,
        vehicle_colour: special?.vehicles?.spec_body_colors?.name,
        vehicle_vin: special?.vehicles?.vin || '',
        vehicle_price: special?.vehicle_price || null,
        vehicle_registration: special?.vehicles?.registration_number || '',
        vehicle_drivetrain: special?.vehicles?.spec_drivetrain?.value || '',
        vehicle_transmission: special?.vehicles?.spec_transmission?.name || '',
        vehicle_fuel: special?.vehicles?.spec_fuel_types?.name || '',
        vehicle_doors: special?.vehicles?.spec_doors.name || '',
        vehicle_image: special?.vehicles?.main_image || '',
        vehicle_images: special?.vehicles?.images || '',
        vehicle_title: special?.title,
        vehicle_body_type: special?.vehicles?.spec_body_type.name || '',
        vehicle_type: special?.vehicle_type,
        dealership_id: dealer?.id || specialDealership?.id,
        dealership_name: dealer?.name || specialDealership?.name,
        contact_firstname: values?.user_name,
        contact_surname: values?.user_name,
        contact_email: values?.email,
        contact_officephone: values?.phone_number,
        contact_cellphone: values?.phone_number,
        comments: values?.message,
        special: 'special',
        dealer_ref:
          dealer?.cms_used_dealer_code ||
          dealer?.cms_new_dealer_code ||
          specialDealership?.cms_used_dealer_code ||
          specialDealership?.cms_new_dealer_code,
        dealer_floor:
          special?.special_for === SPECIAL_FOR_VEHICLE || !special?.special_for
            ? getFloorCodeForSpecials(
                dealer?.cms_used_leads ?? specialDealership?.cms_used_leads,
                dealer?.cms_new_leads ?? specialDealership?.cms_new_leads,
                special?.vehicle_type,
              )
            : special?.special_for === SPECIAL_FOR_SERVICE
              ? dealer?.cms_service_leads
              : dealer?.cms_parts_leads,
        vehicle_used:
          special?.vehicle_type === VehicleType.New
            ? IsUsedVehicleLead.no
            : IsUsedVehicleLead.yes,
        related_specials: special?.id || null,
        source: getSpecialSource(special),
        utm_source: utm_data?.utm_source || '',
        utm_medium: utm_data?.utm_medium || '',
        utm_campaign: utm_data?.utm_campaign || '',
        utm_content: utm_data?.utm_content || '',
        utm_term: utm_data?.utm_term || '',
        utm_cta: utm_data?.utm_cta || '',
        vehicle_testdrive_location_at_contact:
          values?.location_at_contact || false,
        vehicle_testdrive: leadType === 'test-drive',
        utm_dealership: utm_data?.utm_dealership || '',
        fbclid: utm_data?.fbclid || '',
        gclid: utm_data?.gclid || '',
        dealership_micro_site: dealerName,
      };
    } else {
      leadData = {
        ...values,
        dealership_id: dealer?.id,
        dealership_name: dealer?.name,
        variant_title: 'New Kia ' + carData?.main_title,
        variant_name: selectedVariant?.title,
        variant_price: selectedVariant?.price,
        variant_warranty: carData?.images_warranty,
        variant_fuel_consumption: selectedVariant?.fuel_consumption,
        variant_emissions: selectedVariant?.fuel_emissions,
        variant_transmission: selectedVariant?.transmission,
        variant_fuel_type: selectedVariant?.fuel_type,
        variant_doors: selectedVariant?.doors,
        variant_drivetrain: selectedVariant?.drive_train,
        variant_body: selectedVariant?.body_type,
        variant_image: selectedVariant?.image_id,
        vehicle_doors: selectedVariant?.doors,
        vehicle_transmission: selectedVariant?.transmission,
        lead_type: leadType || 'new-cars',
        contact_firstname: values?.user_name,
        contact_surname: values?.user_name,
        contact_email: values?.email,
        contact_officephone: values?.phone_number,
        contact_cellphone: values?.phone_number,
        vehicle_brand:
          selectedVariant?.make ||
          carData?.specification_prices.at(0)?.make ||
          'KIA',
        vehicle_model: carData?.main_title,
        vehicle_range: carData?.main_title,
        vehicle_body_type: carData?.body_type.title,
        vehicle_price:
          selectedVariant?.price ||
          carData?.specification_prices
            ?.sort((a, b) => parseFloat(a.price) - parseFloat(b.price))
            .at(0)?.price,
        dealer_name: dealer?.name,
        dealer_city: dealer?.city.name,
        dealer_province: dealer?.city.province.name,
        comments: values?.message,
        source:
          formItem?.source && formItem?.source !== ''
            ? formItem?.source
            : getNewCarSourceValue(carData || {}),
        dealer_ref: dealer?.cms_new_dealer_code ?? dealer?.cms_new_dealer_code,
        dealer_floor: dealer?.cms_new_leads,
        vehicle_used: IsUsedVehicleLead.no,
        vehicle_mmcode:
          selectedVariant?.mm_code ||
          carData?.specification_prices
            ?.sort((a, b) => parseFloat(a.price) - parseFloat(b?.price))
            .at(0)?.mm_code,
        vehicle_kms: 0,
        vehicle_year: new Date().getFullYear(),
        utm_source: utm_data?.utm_source || '',
        utm_medium: utm_data?.utm_medium || '',
        utm_campaign: utm_data?.utm_campaign || '',
        utm_content: utm_data?.utm_content || '',
        utm_term: utm_data?.utm_term || '',
        utm_cta: utm_data?.utm_cta || '',
        vehicle_tradein: values?.trade_in || false,
        vehicle_finance: values?.finance || false,
        vehicle_testdrive_location_at_contact:
          values?.location_at_contact || false,
        vehicle_testdrive: leadType === 'test-drive',
        utm_dealership: utm_data?.utm_dealership || '',
        fbclid: utm_data?.fbclid || '',
        gclid: utm_data?.gclid || '',
        dealership_micro_site: dealerName,
      };
    }

    try {
      await createLead(leadData);
    } catch (err) {
      console.error(err);

      toast({
        title: ERROR_TITLE,
        description: ERROR_DESCRIPTION,
        status: 'error',
      });
      setIsLoading(false);
      return;
    }

    setIsLoading(false);
    setIsMessageSent(true);

    pushFormSubmittedEvent(leadType, leadData.vehicle_model, wrapper.current);
  };

  const onDone = () => {
    onClose();
    setIsMessageSent(false);
  };

  const wrapper = useRef<HTMLDivElement>(null);

  return (
    <Box className={Styles.enquireNowFormWrapper}>
      <Text fontWeight={'bold'} fontSize={28} mb={4}>
        Enquire Today!
      </Text>
      <Text>I am interested in</Text>
      <div className={Styles.enquireNowFormContent} ref={wrapper}>
        {isMessageSent ? (
          <ThankYouMessageNewCars
            sentAnotherMessage={() => setIsMessageSent(false)}
            onDone={onDone}
          />
        ) : (
          <EnquireFormNewCars
            onSubmitForm={onSubmitForm}
            brandsAndRanges={brandsAndRanges}
            selectedNewCarId={selectedNewCarId || undefined}
            setSelectedNewCarId={setSelectedNewCarId}
            selectedVariant={selectedVariant || undefined}
            setSelectedVariant={setSelectedVariant}
            disableSelectCar={Boolean(
              formItem?.type === 'special' && selectedNewCarId,
            )}
            disableSelectVariant={Boolean(
              formItem?.type === 'special' && specialVariantMMCode && leadType !== 'corporate',
            )}
            availableDealershipIds={
              formItem?.type === 'special'
                ? formItem.special.dealerships.map(
                    (dealership) => dealership.dealerships_id.id,
                  )
                : undefined
            }
            isLoading={isLoading}
            newCars={newCars}
          />
        )}
      </div>
      <PopiaNotice documentPaiaPopia={documentPaiaPopia} />
    </Box>
  );
};
